import { AI_ASSISTANT_SIDEBAR_WIDTH_PX } from '@modules/ai/constants';
import {
  CONTAINER_PADDING_PX,
  LANDSCAPE_PAPER_WIDTH_PX,
  PORTRAIT_PAPER_WIDTH_PX,
  SIDEBAR_WIDTH_PX,
} from '@modules/documents/helpers/getDocumentConfig.helper';
import { useScreenWidth } from '@shared/hooks/useScreenWidth.hook';

import { EItemScreenMode } from '../stores/useItemScreenStore';
import { OPEN_SIDEBAR_PADDING_RIGHT_PX } from '../constants';
import useNavDrawerWithSidebar from './useNavDrawerWithSidebar.hook';

const ITEM_SCREEN_MODE_WITHOUT_SIDEBAR = [undefined, EItemScreenMode.CHANGES];
const CENTERED_MIN_WIDTH_PX = 1024;
// The drawer closes at one screen width for every sidebar - measuring each against its own width
// made comments and AI behave differently on the same screen.
const WIDEST_SIDEBAR_WIDTH_PX = Math.max(SIDEBAR_WIDTH_PX, AI_ASSISTANT_SIDEBAR_WIDTH_PX);

type TArgs = {
  screenMode?: EItemScreenMode;
  hasVersion: boolean;
  isDocument: boolean;
  isAiAssistantAvailable: boolean;
  isSpreadsheet: boolean;
  effectiveLandscape: boolean;
};

type TReturn = {
  maxWidth: string;
  paddingRight: string;
  isAiSidebar: boolean;
  sidebarWidthPx: number;
  isOpenDrawer: boolean;
  setIsOpenDrawer: (isOpen: boolean) => void;
};

const useItemPageLayout = ({
  screenMode,
  hasVersion,
  isDocument,
  isAiAssistantAvailable,
  isSpreadsheet,
  effectiveLandscape,
}: TArgs): TReturn => {
  const screenWidth = useScreenWidth();

  const contentWidthPx = effectiveLandscape ? LANDSCAPE_PAPER_WIDTH_PX : PORTRAIT_PAPER_WIDTH_PX;
  const canRenderSidebar =
    (screenMode !== EItemScreenMode.MOCK_AI_FINDINGS || isDocument) &&
    (screenMode !== EItemScreenMode.AI || isAiAssistantAvailable);
  // No version means no sidebar can render at all, on any tab.
  const hasSidebar = canRenderSidebar && hasVersion && !ITEM_SCREEN_MODE_WITHOUT_SIDEBAR.includes(screenMode);
  const isAiSidebar = hasSidebar && screenMode === EItemScreenMode.AI;
  const sidebarWidthPx = isAiSidebar ? AI_ASSISTANT_SIDEBAR_WIDTH_PX : SIDEBAR_WIDTH_PX;

  const { isOpenDrawer, setIsOpenDrawer } = useNavDrawerWithSidebar({
    hasSidebar,
    contentWidthPx,
    sidebarWidthPx: WIDEST_SIDEBAR_WIDTH_PX,
  });

  let maxWidth: string | number = contentWidthPx + CONTAINER_PADDING_PX;
  if (hasSidebar) maxWidth += sidebarWidthPx;
  // A centered portrait document looks too narrow on a wide screen without a minimum.
  else if (!effectiveLandscape) maxWidth = Math.max(maxWidth, CENTERED_MIN_WIDTH_PX);

  // Spreadsheets: fill available width (viewport minus sidebar if open)
  if (isSpreadsheet) {
    maxWidth = hasSidebar ? `calc(100vw - ${sidebarWidthPx}px - ${CONTAINER_PADDING_PX}px)` : '100vw';
  }

  let paddingRight = '0px';
  if (isOpenDrawer && !isSpreadsheet) {
    if (hasSidebar) paddingRight = `${OPEN_SIDEBAR_PADDING_RIGHT_PX}px`;
    else if (screenWidth < 1400) paddingRight = '170px';
    else if (screenWidth < 1490) paddingRight = '200px';
    else if (screenWidth < 1570) paddingRight = '220px';
    else paddingRight = '300px'; // 315px would be perfectly centered (=sidebar width)
  }

  return {
    maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
    paddingRight,
    isAiSidebar,
    sidebarWidthPx,
    isOpenDrawer,
    setIsOpenDrawer,
  };
};

export default useItemPageLayout;
